import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { CONTENT_ACTIONS } from '@lumapps/contents/constants';
import type { ContentAction } from '@lumapps/contents/types';
import { ContentEditorContext } from '@lumapps/designer-content-editor/context/ContentEditorContext';
import { getWidgetsDirtyTextualFields } from '@lumapps/designer-content-editor/utils/getWidgetsDirtyTextualFields';
import { CONTENT_PROPERTIES_NAMES } from '@lumapps/designer/constants';
import { useContentValidity } from '@lumapps/designer/hooks/useContentValidity';
import { isFeatureEnabled } from '@lumapps/features';
import { useFormContext } from '@lumapps/lumx-forms';
import { useSelector } from '@lumapps/redux/react';
import {
    STRUCTURED_CONTENT_SERIALIZATION_ELEMENTS,
    STRUCTURED_CONTENT_SERIALIZATION_MARKS,
} from '@lumapps/structured-content/constants';
import { getInstanceColorPalette } from '@lumapps/style/ducks/selectors';
import { toCompatibleLanguage } from '@lumapps/translations';
import { contributionLanguagesSelector } from '@lumapps/translations/ducks/selectors';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { slugify } from '@lumapps/utils/string/slugify';
import { useWidgetsAutoTranslate } from '@lumapps/widget-base/hooks/useWidgetsAutoTranslate';
import type { WidgetTranslatableData } from '@lumapps/widget-base/types';
import { parseTranslatedWidgets } from '@lumapps/widget-base/utils/parseTranslatedWidgets';
import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_TITLE_TYPE } from '@lumapps/widget-title/constants';
import { type StructuredContent, document } from '@lumapps/wrex/document/structured-content';
import { getHtmlSerializationOptions } from '@lumapps/wrex/serialization/html/getHtmlConversionOptions';
import { toHTML } from '@lumapps/wrex/serialization/html/toHTML/toHTML';
import { getFirstContentAsString } from '@lumapps/wrex/slate/utils/getFirstContentAsString';
import { Nodes } from '@lumapps/wrex/types/core';
import { normalizeForSlate } from '@lumapps/wrex/utils/normalizeForSlate';

import { MissingTranslationDialog } from '../components/MissingTranslationDialog';
import { getWidgetBodyTranslations } from '../utils/getWidgetBodyTranslations';
import { extractWidgetsData } from '../utils/parseWidgetsToFormValues';

const CONTRIBUTION_HTML_SERIALIZATION_OPTIONS = getHtmlSerializationOptions({
    elements: STRUCTURED_CONTENT_SERIALIZATION_ELEMENTS,
    marks: STRUCTURED_CONTENT_SERIALIZATION_MARKS,
});

/**
 * Hook allowing the display of the missing translation dialog, used to allow the user to auto-translate
 * his content before saving/publishing/scheduling it
 */
export const useMissingTranslationDialog = () => {
    const { widgetPaths, isCreation } = React.useContext(ContentEditorContext);
    const formContext = useFormContext();
    const { formState, getValues, setValue, reset } = formContext ?? {};

    const isEndUserTranslationEnabled = useSelector(isFeatureEnabled('translation-end-user'));
    const colorPalette = useSelector(getInstanceColorPalette);
    const contributionLanguages = useSelector(contributionLanguagesSelector);

    const isContentValid = useContentValidity();
    const inputLang = formContext ? formContext.watch('lang') : undefined;

    const [isDialogOpen, , closeDialog, openDialog] = useBooleanState(false);
    const [translatedLanguages, setTranslatedLanguages] = React.useState<string[]>([]);
    const [currentActionType, setCurrentActionType] = React.useState<ContentAction>(CONTENT_ACTIONS.SAVE);
    const [frozenTargetLanguages, setFrozenTargetLanguages] = React.useState<string[]>([]);
    const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

    const dirtyTextualFields = React.useMemo(
        () => (formState ? getWidgetsDirtyTextualFields(widgetPaths, formState.dirtyFields) : {}),
        [formState, widgetPaths],
    );

    const targetLanguages = React.useMemo(
        () =>
            contributionLanguages.filter((lang) => {
                if (lang === inputLang) {
                    return false;
                }

                // Include this lang only if at least one source-dirty field is NOT dirty in this target
                return Object.values(dirtyTextualFields).some(
                    ({ header, body, footer }) =>
                        (header?.[inputLang] && !header?.[lang]) ||
                        (body?.[inputLang] && !body?.[lang]) ||
                        (footer?.[inputLang] && !footer?.[lang]),
                );
            }),
        [contributionLanguages, inputLang, dirtyTextualFields],
    );

    const widgetsTranslatableData = React.useMemo(() => {
        if (!isDialogOpen || !inputLang || !getValues) {
            return undefined;
        }

        const widgets = Object.values(widgetPaths).map(({ path }) => getValues(path));
        const contentTitle = getValues(CONTENT_PROPERTIES_NAMES.TITLE);
        const extractedData = extractWidgetsData({ widgets, contentTitle });
        const result: WidgetTranslatableData = {};

        for (const [uuid, data] of Object.entries(extractedData)) {
            const dirty = dirtyTextualFields[uuid];
            const titleValue = data.title?.[inputLang];
            let bodyValue = data.body?.translations?.[inputLang];
            const footerValue = data.footer?.[inputLang];

            const shouldAutoTranslateTitle = dirty?.header?.[inputLang] || false;
            const shouldAutoTranslateBody = dirty?.body?.[inputLang] || false;
            const shouldAutoTranslateFooter = dirty?.footer?.[inputLang] || false;

            if (data.type === WIDGET_CONTRIBUTION_TYPE && bodyValue) {
                const richText = bodyValue as StructuredContent;
                const htmlRichText = toHTML(
                    normalizeForSlate(richText.children),
                    CONTRIBUTION_HTML_SERIALIZATION_OPTIONS,
                );
                bodyValue = htmlRichText;
            }

            if (shouldAutoTranslateTitle || shouldAutoTranslateBody || shouldAutoTranslateFooter) {
                result[uuid] = {
                    title: { valueToTranslate: titleValue, shouldAutoTranslate: shouldAutoTranslateTitle },
                    body: {
                        valueToTranslate: typeof bodyValue === 'string' ? bodyValue : undefined,
                        shouldAutoTranslate: shouldAutoTranslateBody,
                    },
                    footer: { valueToTranslate: footerValue, shouldAutoTranslate: shouldAutoTranslateFooter },
                    type: data.type,
                };
            }
        }

        return result;
    }, [isDialogOpen, inputLang, widgetPaths, dirtyTextualFields, getValues]);

    const onTranslationUpdate = React.useCallback(
        async (translatedValues: Record<string, (string | undefined)[]>) => {
            if (!setValue || !reset) {
                return;
            }

            parseTranslatedWidgets(translatedValues, {
                colorPalette,
                onWidget: (initTargetLang, { widgetId, widgetType, headerText, bodyValue, footerText }) => {
                    const targetLang = toCompatibleLanguage(initTargetLang);
                    const widgetPathEntry = widgetPaths[widgetId];
                    if (!widgetPathEntry) {
                        return;
                    }

                    const { path } = widgetPathEntry;

                    // Widget header
                    if (headerText) {
                        setValue(`${path}.title.${targetLang}`, headerText.trim());
                    }

                    // Widget body
                    if (bodyValue !== undefined) {
                        const trimmedBodyValue = typeof bodyValue === 'string' ? bodyValue.trim() : bodyValue;
                        if (widgetType === WIDGET_TITLE_TYPE) {
                            // Content title lives at root 'title' field
                            setValue(`title.${targetLang}`, trimmedBodyValue);

                            // Generate the slug from the translated title. On creation the slug always
                            // follows the title; on edition an existing slug is preserved.
                            const currentSlug = getValues(`${CONTENT_PROPERTIES_NAMES.URL}.${targetLang}`);
                            if ((isCreation || !currentSlug) && typeof trimmedBodyValue === 'string') {
                                setValue(`${CONTENT_PROPERTIES_NAMES.URL}.${targetLang}`, slugify(trimmedBodyValue));
                            }
                        } else {
                            const bodyKey = getWidgetBodyTranslations(widgetType, {})?.key;

                            if (bodyKey) {
                                const widgetBodyValue =
                                    widgetType === WIDGET_CONTRIBUTION_TYPE
                                        ? document.parse({
                                              children: bodyValue,
                                              title: getValues(`${CONTENT_PROPERTIES_NAMES.TITLE}.${targetLang}`) || '',
                                              description: getFirstContentAsString(bodyValue as Nodes),
                                          })
                                        : trimmedBodyValue;

                                setValue(`${path}.properties.${bodyKey}.${targetLang}`, widgetBodyValue);
                            }
                        }
                    }

                    // Widget footer
                    if (footerText) {
                        setValue(`${path}.properties.more.label.${targetLang}`, footerText.trim());
                    }
                },
            });

            // Reset defaults to current values so future edits of users will count as dirty.
            // Useful in the case the user cancel the flow after an auto-translate
            reset(getValues(), { keepErrors: true, keepTouched: true, keepIsSubmitted: true });

            setTranslatedLanguages((prev) => [...new Set([...prev, ...Object.keys(translatedValues)])]);
        },
        [setValue, reset, colorPalette, widgetPaths, getValues, isCreation],
    );

    const {
        isLoading,
        onAutoTranslate: onWidgetsAutoTranslate,
        onHardReset,
    } = useWidgetsAutoTranslate({
        sourceLanguage: inputLang,
        targetLanguages,
        widgetsTranslatableData,
        onTranslationUpdate,
    });

    const handleConfirm = React.useCallback(() => {
        onHardReset();
        setTranslatedLanguages([]);
        closeDialog();
        resolveRef.current?.(true);
        resolveRef.current = null;
    }, [closeDialog, onHardReset]);

    const handleCancel = React.useCallback(() => {
        onHardReset();
        setTranslatedLanguages([]);
        closeDialog();
        resolveRef.current?.(false);
        resolveRef.current = null;
    }, [closeDialog, onHardReset]);

    const handleTranslateChanges = React.useCallback(() => {
        onWidgetsAutoTranslate();
    }, [onWidgetsAutoTranslate]);

    /**
     * The dialog should be displayed only when all conditions are met :
     * - we are in the Page Writer Editor (inside a form context + a ContentEditorContext)
     * - auto-translate FF is enabled
     * - the instance has more than one language
     */
    const shouldDisplayDialog =
        !!formState && !isEmpty(widgetPaths) && isEndUserTranslationEnabled && contributionLanguages.length > 1;

    /** Check for missing translation on the updated widgets and
     * open the MissingTranslationDialog if needed
     */
    const checkMissingTranslation = React.useCallback(
        (actionType: ContentAction): Promise<boolean> => {
            if (
                // Feature flags and form context must be available
                !isContentValid ||
                !formState ||
                !isEndUserTranslationEnabled ||
                // No widget textual fields have been modified
                Object.keys(dirtyTextualFields).length === 0 ||
                // No target languages need translation (e.g. user switched input language)
                targetLanguages.length === 0 ||
                // All target languages were already auto-translated in a previous attempt
                targetLanguages.every((lang) => translatedLanguages.includes(lang))
            ) {
                return Promise.resolve(true);
            }

            const dirtyLanguages = new Set(
                Object.values(dirtyTextualFields).flatMap(({ header, body, footer }) =>
                    [header, body, footer]
                        .filter((field): field is Record<string, boolean> => !!field)
                        .flatMap(Object.keys),
                ),
            );

            if (dirtyLanguages.size >= contributionLanguages.length) {
                return Promise.resolve(true);
            }

            return new Promise<boolean>((resolve) => {
                onHardReset();
                setFrozenTargetLanguages(targetLanguages);
                resolveRef.current = resolve;
                setCurrentActionType(actionType);
                openDialog();
            });
        },
        [
            isContentValid,
            formState,
            isEndUserTranslationEnabled,
            dirtyTextualFields,
            contributionLanguages.length,
            onHardReset,
            openDialog,
            targetLanguages,
            translatedLanguages,
        ],
    );

    const missingTranslationDialog = shouldDisplayDialog ? (
        <MissingTranslationDialog
            isOpen={isDialogOpen}
            isLoading={isLoading}
            targetLanguages={frozenTargetLanguages}
            translatedLanguages={translatedLanguages}
            currentActionType={currentActionType}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            onTranslateChanges={handleTranslateChanges}
        />
    ) : null;

    return { checkMissingTranslation, missingTranslationDialog };
};
